'use client';
import React, { useEffect, useRef } from 'react';

const evolutionCards = [
  {
    tag: 'Gestion',
    title: 'Inventaire digital',
    description: 'Listez et gérez le contenu de votre logement en format numérique, toujours à jour.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
      </svg>
    ),
  },
  {
    tag: 'Protection',
    title: 'État des lieux',
    description: "Documentez l'état de votre bien avant et après chaque séjour, avec preuves photos.",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  {
    tag: 'Sécurité',
    title: 'Protection litiges',
    description: 'En cas de désaccord, disposez d\'un dossier structuré et d\'éléments de preuve solides.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
      </svg>
    ),
  },
  {
    tag: 'Visibilité',
    title: 'Page Google Business',
    description: 'Renforcez votre présence locale et attirez des voyageurs en dehors des plateformes.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
  },
  {
    tag: 'Performance',
    title: 'Optimisation d\'annonce',
    description: 'Améliorez votre visibilité sur les plateformes de réservation grâce à des recommandations ciblées.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" />
      </svg>
    ),
  },
  {
    tag: 'Multipropriété',
    title: 'Outils avancés',
    description: 'Pour les multipropriétaires et conciergeries : gestion centralisée de plusieurs biens.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
        <path d="M9 22V12h6v10" />
      </svg>
    ),
  },
];

export default function EvolutionSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll('.animate-on-scroll')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 md:py-40 relative z-10 bg-background border-t border-primary/8">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-7 animate-on-scroll anim-in" style={{ animationPlayState: 'paused' }}>
            <span className="text-[10px] uppercase tracking-ultra text-accent font-medium block mb-5">
              Une évolution naturelle
            </span>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-tight text-foreground">
              Le livret évolue{' '}
              <span className="italic text-teal-gradient">avec vous.</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex items-end animate-on-scroll anim-in delay-100" style={{ animationPlayState: 'paused' }}>
            <p className="text-sm text-foreground-muted leading-relaxed">
              Commencez simplement avec le livret gratuit. Si un jour vous souhaitez aller plus loin, 
              des fonctionnalités complémentaires sont disponibles — sans aucune obligation.
            </p>
          </div>
        </div>

        {/* Horizontal scroll cards */}
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto pb-6 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {evolutionCards?.map((card, i) => (
            <div
              key={card?.title}
              className="snap-start shrink-0 w-72 md:w-80 border border-primary/12 bg-white p-8 flex flex-col justify-between group hover:border-primary/30 transition-all duration-400 animate-on-scroll anim-in"
              style={{ animationPlayState: 'paused', animationDelay: `${i * 0.07}s` }}
            >
              <div>
                <div className="flex items-start justify-between mb-6">
                  <span className="text-[9px] uppercase tracking-ultra border border-accent/30 text-accent-dark px-2 py-1">
                    {card?.tag}
                  </span>
                  <span className="text-foreground-subtle group-hover:text-primary transition-colors duration-300">
                    {card?.icon}
                  </span>
                </div>
                <h3 className="font-serif-display text-2xl text-foreground mb-4 leading-tight">{card?.title}</h3>
                <p className="text-sm text-foreground-muted leading-relaxed">{card?.description}</p>
              </div>
              <div className="mt-8 h-px w-0 bg-primary group-hover:w-full transition-all duration-500" />
            </div>
          ))}
        </div>

        {/* Scroll hint */}
        <div className="flex items-center gap-3 mt-4 animate-on-scroll anim-in" style={{ animationPlayState: 'paused' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-foreground-subtle">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
          <span className="text-[10px] uppercase tracking-ultra text-foreground-subtle">Faites défiler pour découvrir</span>
        </div>

        {/* Reassurance note */}
        <div
          className="mt-16 p-8 md:p-10 bg-primary/4 border border-primary/12 animate-on-scroll anim-in"
          style={{ animationPlayState: 'paused' }}
        >
          <div className="flex items-start gap-5">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary mt-0.5 shrink-0">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <div>
              <p className="font-medium text-foreground text-sm mb-2">Aucune obligation de montée en gamme</p>
              <p className="text-sm text-foreground-muted leading-relaxed max-w-2xl">
                Le livret gratuit est complet et fonctionnel. Les options évolutives sont présentées ici 
                à titre informatif, pour les propriétaires qui souhaiteraient aller plus loin à leur propre rythme. 
                Aucune pression, aucun abonnement imposé.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}